import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, ArrowUpRight, Layers, Cog, Zap, Award, Globe2, ShieldCheck, Factory, Wrench } from "lucide-react";
import api from "@/lib/api";
import { ProductCard } from "@/components/ProductCard";
import { ProcessSection } from "@/components/ProcessSection";
import { useContent, renderRich } from "@/context/ContentContext";

const HERO_BG = "https://images.unsplash.com/photo-1774023032450-a5ccaecdf158";

const ICONS = { Cog, Layers, Zap, Award, Globe2, ShieldCheck, Factory, Wrench };
const Icon = ({ name, ...rest }) => {
    const C = ICONS[name] || Cog;
    return <C {...rest} />;
};

const Home = () => {
    const c = useContent();
    const [featured, setFeatured] = useState([]);

    useEffect(() => {
        api.get("/products", { params: { featured: true } })
            .then(({ data }) => setFeatured(data.slice(0, 6)))
            .catch(() => {});
    }, []);

    const hero = c.hero || {};
    const stats = c.stats || [];
    const cap = c.capabilities || {};
    const featuredS = c.featured || {};
    const cats = c.categories_section || {};
    const why = c.why || {};
    const bottom = c.bottom_cta || {};

    return (
        <div data-testid="home-page">
            {/* Hero */}
            <section className="relative bg-gray-900 text-white overflow-hidden">
                <div className="absolute inset-0">
                    <img src={HERO_BG} alt="" className="w-full h-full object-cover opacity-40" />
                    <div className="absolute inset-0 bg-gradient-to-b from-gray-900/60 via-gray-900/60 to-gray-900" />
                </div>
                <div className="relative max-w-7xl mx-auto px-6 md:px-12 pt-28 pb-32 md:pt-44 md:pb-48">
                    <div className="overline text-red-600 mb-8" data-testid="hero-overline">{hero.overline}</div>
                    <h1 className="font-display font-black text-5xl md:text-7xl lg:text-[88px] leading-[0.95] uppercase tracking-tighter max-w-5xl whitespace-pre-line">
                        {renderRich(hero.headline)}
                    </h1>
                    <p className="mt-10 max-w-2xl text-base md:text-lg text-gray-300 leading-relaxed">
                        {hero.description}
                    </p>
                    <div className="mt-12 flex flex-wrap gap-4">
                        <Link to="/products" className="btn-primary" data-testid="hero-cta-products">
                            {hero.primary_cta} <ArrowRight size={16} />
                        </Link>
                        <Link to="/contact" className="btn-outline border-white text-white hover:bg-white hover:text-gray-900" data-testid="hero-cta-quote">
                            {hero.secondary_cta}
                        </Link>
                    </div>
                </div>

                <div className="relative border-t border-white/10 bg-gray-900">
                    <div className="max-w-7xl mx-auto px-6 md:px-12 grid grid-cols-2 md:grid-cols-4">
                        {stats.map((n, i) => (
                            <div key={i} className={`py-10 ${i !== 0 ? "md:border-l border-white/10" : ""} ${i % 2 === 1 ? "border-l border-white/10 md:border-l" : ""}`} data-testid={`stat-${i}`}>
                                <div className="font-display text-3xl md:text-5xl font-black tracking-tight">{n.value}</div>
                                <div className="overline text-gray-400 mt-2">{n.label}</div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Bottle Manufacturing Process */}
            <ProcessSection />


            {/* Bottle Manufacturing Process */}
            <ProcessSection />

            {/* Capabilities */}
            <section className="max-w-7xl mx-auto px-6 md:px-12 py-24 md:py-32">
                <div className="flex flex-col md:flex-row md:items-end justify-between gap-8 mb-16">
                    <div>
                        <div className="overline text-red-600 mb-4">{cap.overline}</div>
                        <h2 className="font-display font-black uppercase text-4xl md:text-5xl tracking-tight max-w-3xl">
                            {renderRich(cap.heading)}
                        </h2>
                    </div>
                    <p className="text-gray-600 max-w-md">{cap.paragraph}</p>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 border-t border-l border-gray-200">
                    {(cap.items || []).map((cItem, i) => (
                        <div key={i} className="border-r border-b border-gray-200 p-8 group hover:bg-gray-900 transition-colors duration-300" data-testid={`capability-${i}`}>
                            <Icon name={cItem.icon} className="text-red-600 group-hover:text-red-400" size={28} strokeWidth={1.5} />
                            <h3 className="font-display text-2xl font-bold mt-6 mb-3 group-hover:text-white transition-colors">{cItem.title}</h3>
                            <p className="text-sm text-gray-600 group-hover:text-gray-300 leading-relaxed transition-colors">{cItem.body}</p>
                        </div>
                    ))}
                </div>
            </section>

            {/* Featured */}
            <section className="bg-gray-50 border-y border-gray-200">
                <div className="max-w-7xl mx-auto px-6 md:px-12 py-24 md:py-32">
                    <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-14">
                        <div>
                            <div className="overline text-red-600 mb-4">{featuredS.overline}</div>
                            <h2 className="font-display font-black uppercase text-4xl md:text-5xl tracking-tight">
                                {renderRich(featuredS.heading)}
                            </h2>
                        </div>
                        <Link to="/products" className="font-mono text-xs uppercase tracking-[0.22em] text-gray-900 hover:text-red-600 inline-flex items-center gap-2" data-testid="featured-view-all">
                            {featuredS.view_all} <ArrowUpRight size={16} />
                        </Link>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-10">
                        {featured.map((p, i) => (
                            <ProductCard key={p.id} product={p} index={i} />
                        ))}
                    </div>
                </div>
            </section>

            {/* Categories */}
            <section className="max-w-7xl mx-auto px-6 md:px-12 py-24 md:py-32">
                <div className="overline text-red-600 mb-4">{cats.overline}</div>
                <h2 className="font-display font-black uppercase text-4xl md:text-5xl tracking-tight mb-14">
                    {renderRich(cats.heading)}
                </h2>

                <div className="grid md:grid-cols-2 gap-6 md:gap-10">
                    {[
                        { slug: "thermos-flask-production-lines", title: "Thermos & Flask Production Lines", img: "https://images.pexels.com/photos/12951623/pexels-photo-12951623.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940" },
                        { slug: "cup-printing-machines", title: "Cup Printing Machines", img: "https://images.unsplash.com/photo-1572119865084-43c285814d63?auto=format&fit=crop&w=1200&q=80" },
                    ].map((cat) => (
                        <Link key={cat.slug} to={`/products?category=${cat.slug}`} className="group block relative overflow-hidden" data-testid={`cat-tile-${cat.slug}`}>
                            <div className="product-image-wrap aspect-[5/4]">
                                <img src={cat.img} alt={cat.title} />
                            </div>
                            <div className="absolute inset-0 bg-gradient-to-t from-gray-900/80 to-transparent" />
                            <div className="absolute bottom-0 left-0 right-0 p-8 text-white flex items-end justify-between">
                                <h3 className="font-display font-black text-3xl md:text-4xl tracking-tight uppercase max-w-xs">
                                    {cat.title}
                                </h3>
                                <ArrowUpRight className="shrink-0 mb-1 group-hover:text-red-600 transition-colors" size={28} />
                            </div>
                        </Link>
                    ))}
                </div>
            </section>

            {/* Why */}
            <section className="bg-gray-900 text-white">
                <div className="max-w-7xl mx-auto px-6 md:px-12 py-24 md:py-32 grid md:grid-cols-12 gap-12">
                    <div className="md:col-span-5">
                        <div className="overline text-red-600 mb-4">{why.overline}</div>
                        <h2 className="font-display font-black uppercase text-4xl md:text-5xl tracking-tight">
                            {renderRich(why.heading)}
                        </h2>
                    </div>
                    <div className="md:col-span-7 grid sm:grid-cols-2 gap-x-12 gap-y-10">
                        {(why.items || []).map((r, i) => (
                            <div key={i} data-testid={`why-${i}`}>
                                <Icon name={r.icon} className="text-red-600 mb-4" size={24} strokeWidth={1.5} />
                                <div className="font-display font-bold text-xl mb-2">{r.title}</div>
                                <p className="text-sm text-gray-400 leading-relaxed">{r.body}</p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* CTA */}
            <section className="border-t border-gray-200">
                <div className="max-w-7xl mx-auto px-6 md:px-12 py-24 md:py-32 grid md:grid-cols-12 gap-10 items-end">
                    <div className="md:col-span-8">
                        <div className="overline text-red-600 mb-4">{bottom.overline}</div>
                        <h2 className="font-display font-black uppercase text-4xl md:text-6xl tracking-tighter whitespace-pre-line">
                            {renderRich(bottom.heading)}
                        </h2>
                    </div>
                    <div className="md:col-span-4 flex md:justify-end">
                        <Link to="/contact" className="btn-dark" data-testid="footer-cta-quote">
                            {bottom.button} <ArrowRight size={16} />
                        </Link>
                    </div>
                </div>
            </section>
        </div>
    );
};

export default Home;
